import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { noop } from 'lodash';
import {
    CustomOrderData,
    CustomOrderSubCategoryOption,
    FederatedSeller,
    useCurrentPageName,
    useLocalizationContext,
    useSellerData,
} from '@fiverr-private/seller_pages_toolbox';
import { useCustomOrderModal, useCustomOrderModalClose } from '../hooks/useCustomOrderModal';

const LazyCustomOrderModal = lazy(
    () => import(/* webpackChunkName: 'CustomOrderModalEntry' */ './CustomOrderModalEntry')
);

const categoriesDataSerializer = (data: CustomOrderData | null) =>
    data && data.dds
        ? data.dds.categories.options.map((item) => ({
              value: item.val,
              label: item.text,
              subCategories: data.dds.subCategories.options[item.val].map((subItem: CustomOrderSubCategoryOption) => ({
                  value: subItem.val,
                  label: subItem.text,
                  metadata: data.metaData[item.val] || [],
              })),
          }))
        : [];

export const CustomOrderModal = () => {
    const pageName = useCurrentPageName();
    const { currency } = useLocalizationContext();
    const modalData = useCustomOrderModal();
    const openModalRef = useRef(noop);
    const closeModal = useCustomOrderModalClose();
    const [initModal, setInitModal] = useState(modalData.shouldOpen);
    const { username, displayName, profileImageUrl } = useSellerData(selectSellerSlice);

    useEffect(() => {
        if (modalData.shouldOpen && modalData.data) {
            setInitModal(true);
            openModalRef.current();
        }
    }, [modalData.shouldOpen, modalData.data]);

    if (!initModal || !modalData.data) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <LazyCustomOrderModal
                autoOpen
                source={pageName}
                currency={currency}
                trigger={(open) => {
                    openModalRef.current = open;
                }}
                categoriesData={modalData.data.categoriesData || categoriesDataSerializer(modalData.data)}
                onClose={closeModal}
                seller={{ name: username, image: profileImageUrl, displayName }}
                submitPath={modalData.data.submitPath}
                persistType={modalData.data.persistType}
                uploadOptions={modalData.data.attachmentsUploadConfig}
            />
        </Suspense>
    );
};

const selectSellerSlice = (seller: FederatedSeller) => ({
    username: seller.user.name,
    displayName: seller.user.profile.displayName,
    profileImageUrl: seller.user.profileImageUrl,
});
